import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { attachScansColumns } from 'src/assets/static/invoice_details/scans';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import Button from 'src/components/design-system/Button';
import useWindowSize from 'src/hooks/useWindowSize';
import useGetUnattachmentsDocumentsApi from 'src/services/useGetUnattachedDocumentsApi';
import { modifyRequest } from 'src/utils/requestInterceptor';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onAttach: (attachIds: number[]) => void;
  companyId: string;
  isInvoices: boolean;
  refreshParent?: VoidFunction;
}

export const initialScanManagerDataState = (isInvoice: boolean): State => {
  let initialFilter: CompositeFilterDescriptor[] = [
    {
      logic: 'and',
      filters: [{ field: 'receiptId', value: '', operator: 'isnull' }],
    },
  ];
  if (isInvoice) {
    initialFilter = [
      ...initialFilter,
      { logic: 'and', filters: [{ field: 'ocrStatus', value: '', operator: 'isnullorempty' }] },
      { logic: 'and', filters: [{ field: 'isDeleted', value: 'false', operator: 'eq' }] },
    ];
  }
  return {
    skip: 0,
    take: 20,
    sort: [
      {
        field: 'originalFile',
        dir: 'asc',
      },
    ],

    filter: {
      logic: 'and',
      filters: initialFilter,
    },
  };
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [5, 10, 20, 50, 100],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};
const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
} as const;
const dateArr = ['creationDate'];
const AttachExistingDocumentModal: FC<Props> = ({
  open,
  onClose,
  onAttach,
  companyId,
  isInvoices,
  refreshParent = () => {},
}) => {
  const [dataState, setDataState] = useState(initialScanManagerDataState(isInvoices));
  const { areDocumentsLoading, getUnattachedDocuments, documentsResponse, setDocumentsResponse } =
    useGetUnattachmentsDocumentsApi();
  const { height } = useWindowSize();

  useEffect(() => {
    if (open) {
      getUnattachedDocuments({
        body: modifyRequest(dataState, dateArr),
        companyId: companyId,
        isInvoices,
      });
    }
  }, [companyId, dataState, getUnattachedDocuments, isInvoices, open]);

  const handleDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const handleSelectionChange = useCallback(
    (e: GridSelectionChangeEvent) => {
      setDocumentsResponse((prev) => ({
        ...prev,
        items: prev.items.map((item) => ({
          ...item,
          ...(item.rowId === e.dataItem?.rowId && {
            selected: !item.selected,
          }),
        })),
      }));
    },
    [setDocumentsResponse]
  );

  const handleHeaderSelectionChange = useCallback(
    (e: GridHeaderSelectionChangeEvent) => {
      const { checked } = e.syntheticEvent.target as any;
      setDocumentsResponse((prev) => ({
        ...prev,
        items: prev.items.map((item) => ({
          ...item,
          selected: checked,
        })),
      }));
    },
    [setDocumentsResponse]
  );

  const handleClose = useCallback(() => {
    onClose();
    setDocumentsResponse((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({
        ...item,
        selected: false,
      })),
    }));
  }, [onClose, setDocumentsResponse]);

  const handleAttachDocuments = useCallback(() => {
    onAttach(
      documentsResponse?.items?.filter((item) => item.selected).map((item) => item.attachId)
    );
    refreshParent?.();
    handleClose();
  }, [onAttach, documentsResponse?.items, refreshParent, handleClose]);

  const isItemSelected = useMemo(
    () => documentsResponse?.items?.some((item) => item?.selected),
    [documentsResponse?.items]
  );

  return (
    <Modal title="Attach Existing Documents" closeModal={handleClose} visible={open} width="80vw">
      <GridWrapper
        columns={attachScansColumns}
        total={documentsResponse?.totalRecords}
        dataSource={documentsResponse?.items}
        dataState={dataState}
        onDataStateChange={handleDataStateChange}
        loading={areDocumentsLoading}
        pageable={gridPagerSettings}
        height={`${height - 186}px`}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        {...defaultProps}
      />
      <DialogActionsBar>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button color="info" onClick={handleAttachDocuments} disabled={!isItemSelected}>
            Attach Documents
          </Button>
          <Button color="info" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </Modal>
  );
};

export default AttachExistingDocumentModal;
